import { Apollo } from 'apollo-angular'
import { ApolloQueryResult } from '@apollo/client/core'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { JwtHelperService } from '@auth0/angular-jwt'
import { AuthError, UserAgentApplication, authResponseCallback } from 'msal'
import { CookieService } from 'ngx-cookie-service'
import { UserStatus } from 'common/aqsTypes'
import { redactSecrets } from 'common/redact'
import { UserRole } from 'src/types/graphql-global-types'
import { CommService } from '../core/comm.service'
import { DatagridStateService } from '../core/datagrid-state.service'
import { MessageError } from '../core/errors/errors'
import { issueTracker } from '../core/errors/issueTracker'
import { TimezoneService } from '../timezone.service'
import nativeLoginGql from './login.gql'
import microsoftLoginGql from './microsoftLogin.gql'
import selfStatusGql from './selfStatus.gql'
import { selfStatus } from './types/selfStatus'
import { login, login_login_user } from './types/login'
import { environment } from '../../environments/environment'
import { microsoftLogin, microsoftLoginVariables } from './types/microsoftLogin'
import { usersDetails_user_by_pk } from '../home/users-details/types/usersDetails'

/** what Microsoft answered a sign-in redirect with, as MSAL hands it over */
type MicrosoftReply = { idToken: string } | { error: AuthError }

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _loginData?: login
  private myMSALObj: UserAgentApplication
  private redirectUrl: string
  /** Microsoft's answer on the page it redirected back to, until completeMicrosoftSignIn() */
  private microsoftReply?: MicrosoftReply

  constructor(
    private router: Router,
    private apollo: Apollo,
    private cookieService: CookieService,
    private timezoneService: TimezoneService,
    private datagridState: DatagridStateService,
    private comm: CommService,
  ) {
    // every report to the issue tracker names whoever is logged in then
    issueTracker.identifyWith(() => this.user)
    // On the page Microsoft redirects back to, MSAL calls this from
    // handleRedirectCallback below, i.e. while this service is being built.
    // That can be before Angular has built the rest of the app: the global
    // error handler needs this service (through TerminologyService) and is
    // created first, before the `app` Apollo client exists and before errors
    // are reported. So the answer is only kept here, and
    // completeMicrosoftSignIn() acts on it once the app has started.
    const authRedirectCallBack: authResponseCallback = (error, response) => {
      if (error) {
        console.log(error)
        this.microsoftReply = { error }
      } else {
        // never log raw tokens
        console.log(redactSecrets(response))
        if (response.tokenType === 'id_token') {
          if (this.myMSALObj.getAccount()) {
            console.log(redactSecrets(this.myMSALObj.getAccount()))
            // The server verifies the raw ID token (signature, issuer,
            // audience, expiry) and takes the identity from its claims; the
            // Account object MSAL derives from it is not sent.
            this.microsoftReply = { idToken: response.idToken.rawIdToken }
          }
        }
      }
    }
    this.myMSALObj = new UserAgentApplication(environment.msalConfig)
    this.myMSALObj.handleRedirectCallback(authRedirectCallBack)
  }

  /**
   * Finishes a Microsoft sign-in on the page Microsoft redirected back to:
   * exchanges the ID token for an AQS session, or fails with what Microsoft
   * answered. Does nothing on any other page load.
   *
   * Run once at startup by MICROSOFT_SIGN_IN_INITIALIZER, never from the
   * constructor: there (3.22.0) it ran before the `app` client existed, and
   * the failure was dropped before Angular reported errors, so the sign-in
   * ended on the login page without a word.
   */
  async completeMicrosoftSignIn(): Promise<void> {
    const reply = this.microsoftReply
    this.microsoftReply = undefined
    if (!reply) {
      return
    }
    if ('error' in reply) {
      // Microsoft's description (with its AADSTS code) goes to the tracker's
      // trail rather than the toast: for a nonce mismatch it names both nonces
      issueTracker.leaveBreadcrumb(
        'Microsoft sign-in answer',
        {
          errorCode: reply.error.errorCode,
          errorMessage: reply.error.errorMessage,
        },
        'error',
      )
      throw new MessageError(
        `Your Microsoft sign-in did not complete (${reply.error.errorCode}). Please sign in again; if this keeps happening, contact AQS support.`,
      )
    }
    await this.handleMSLoginResponse(reply.idToken)
  }

  async handleMSLoginResponse(idToken: string) {
    // contact app server to issue a login token
    const variables: microsoftLoginVariables = { idToken }
    return new Promise<void>((resolve, reject) => {
      this.apollo
        .use('app')
        .query<microsoftLogin>({
          query: microsoftLoginGql,
          variables,
        })
        .subscribe({
          next: (result: ApolloQueryResult<microsoftLogin>) => {
            if (result.errors) {
              reject(result.errors[0])
              console.log('graphql error')
              return
            }
            this.loginData = { login: result.data.microsoftLogin }
            this.router.navigateByUrl(this.redirectUrl || '/')
            resolve()
          },
          error: (e) => reject(e),
        })
    })
  }

  msalLogin(redirectUrl?: string) {
    this.redirectUrl = redirectUrl
    this.myMSALObj.loginRedirect({
      // for more, visit https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-core/docs/scopes.md
      scopes: ['openid', 'profile', 'User.Read'],
    })
  }

  set loginData(data: login | undefined) {
    this._loginData = data
    if (data) {
      this.cookieService.set('loginData', JSON.stringify(this._loginData))
    } else {
      this.cookieService.delete('loginData', '/')
    }
  }

  get loginData(): login | undefined {
    const cookievalue = this.cookieService.get('loginData')
    try {
      this._loginData = JSON.parse(cookievalue)
    } catch (err) {
      return undefined
    }
    return this._loginData
  }

  tokenExpired(): boolean {
    const helper = new JwtHelperService()
    const isExpired = helper.isTokenExpired(this.token)
    return isExpired
  }

  get isLoggedIn(): boolean {
    return !!this.loginData && !this.tokenExpired()
  }

  get user(): login_login_user | undefined {
    return this.loginData?.login.user
  }

  get token(): string | undefined {
    return this.loginData?.login.token
  }

  get permissions(): string[] | undefined {
    return this.loginData?.login.permissions
  }

  async login(
    variables: { username: string; password: string },
    url: string,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apollo
        .use('app')
        .query<login>({
          query: nativeLoginGql,
          variables,
        })
        .subscribe({
          next: (result: ApolloQueryResult<login>) => {
            if (result.errors) {
              reject(result.errors[0])
              console.log('graphql error')
              return
            }
            this.loginData = result.data
            console.log(redactSecrets(result.data))
            this.router.navigateByUrl(url)
            resolve()
          },
          error: (e) => reject(e),
        })
    })
  }

  private lastStatusCheck = 0
  private statusCheckInFlight = false
  private static readonly STATUS_CHECK_MS = 30_000

  /**
   * Throttled self-check that the logged-in account is still active; ejects
   * soft-deleted users on their next action. UX-level only: the JWT
   * has no expiry, so an already-issued token keeps working against Hasura
   * until AQS_JWT_SIGN_TOKEN is rotated.
   */
  checkAccountActive(): void {
    if (!this.isLoggedIn || !this.user) return
    const now = Date.now()
    if (
      this.statusCheckInFlight ||
      now - this.lastStatusCheck < AuthService.STATUS_CHECK_MS
    ) {
      return
    }
    this.lastStatusCheck = now
    this.statusCheckInFlight = true
    this.apollo
      .query<selfStatus>({
        query: selfStatusGql,
        variables: { id: this.user.id },
      })
      .subscribe({
        next: (result) => {
          this.statusCheckInFlight = false
          if (result.errors) return // fail open; auth errors surface elsewhere
          const status = result.data?.user_by_pk?.status
          if (status !== UserStatus.active) {
            this.forceLogout(
              'You have been logged out because your account is no longer active. Please contact your administrator.',
            )
          }
        },
        error: () => {
          this.statusCheckInFlight = false // fail open on network errors
        },
      })
  }

  forceLogout(message: string) {
    this.logout()
    // alert after logout: clearLocals() must not wipe the message
    this.comm.alert('warning', message, -1)
  }

  logout() {
    this.clearLocals()
    this.router.navigateByUrl('/')
  }

  clearLocals() {
    this.loginData = undefined
    localStorage.clear()
    this.cookieService.deleteAll()
    this.datagridState.clear()
  }

  checkrole(roles: UserRole[] | UserRole): boolean {
    if (!this.user) return false
    if (!Array.isArray(roles)) {
      roles = [roles]
    }
    if (!this.user) return false
    return roles.includes(this.user.role)
  }

  isNativeAccount(
    user?: { accountType: string } | Partial<usersDetails_user_by_pk>,
  ) {
    return (user || this.user)?.accountType === 'native'
  }
}
